import axios from "axios";
import { useCallback, useState } from "react";
import { getAuthToken } from "../context/AuthContext";

const baseUrl = process.env.REACT_APP_BACKEND_URL + "api/learning-platform";

const api = axios.create({
  baseURL: baseUrl,
});

api.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const useLearningPlatform = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Branding & domain
  const getSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings");
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const updateSettings = useCallback(async (settings) => {
    setLoading(true);
    try {
      const { data } = await api.put("/settings", settings);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  // Courses
  const getCourses = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/courses");
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const getCourseById = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/courses/${id}`);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const createCourse = useCallback(async (course) => {
    setLoading(true);
    try {
      const { data } = await api.post("/courses", course);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const updateCourse = useCallback(async (id, course) => {
    setLoading(true);
    try {
      const { data } = await api.put(`/courses/${id}`, course);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const deleteCourse = useCallback(async (id) => {
    setLoading(true);
    try {
      await api.delete(`/courses/${id}`);
      setLoading(false);
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  // Modules
  const getModules = useCallback(async (courseId) => {
    const { data } = await api.get(`/courses/${courseId}/modules`);
    return data;
  }, []);

  const createModule = useCallback(async (courseId, payload) => {
    const { data } = await api.post(`/courses/${courseId}/modules`, payload);
    return data;
  }, []);

  const updateModule = useCallback(async (id, payload) => {
    const { data } = await api.put(`/modules/${id}`, payload);
    return data;
  }, []);

  const deleteModule = useCallback(async (id) => {
    await api.delete(`/modules/${id}`);
  }, []);

  // Lessons (under a course)
  const getLessons = useCallback(async (courseId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/courses/${courseId}/lessons`);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const createLesson = useCallback(async (courseId, lesson) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/courses/${courseId}/lessons`, lesson);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const updateLesson = useCallback(async (lessonId, lesson) => {
    setLoading(true);
    try {
      const { data } = await api.put(`/lessons/${lessonId}`, lesson);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const deleteLesson = useCallback(async (lessonId) => {
    setLoading(true);
    try {
      await api.delete(`/lessons/${lessonId}`);
      setLoading(false);
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  // Multipart upload with progress callback (raw XHR for progress)
  const uploadLessonVideo = useCallback((lessonId, file, durationSeconds, onProgress) => {
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append("file", file);
      if (durationSeconds) form.append("duration_seconds", String(durationSeconds));
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${baseUrl}/lessons/${lessonId}/video`);
      const token = getAuthToken();
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable && onProgress) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch { resolve({}); }
        } else {
          reject(new Error(`Upload failed (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error("Netwerkfout tijdens upload"));
      xhr.send(form);
    });
  }, []);

  // Students
  const getStudents = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/students");
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const getStudentProgress = useCallback(async (studentId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${studentId}/progress`);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  // Community / discussions
  const getDiscussions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/discussions");
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const getDiscussion = useCallback(async (id) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/discussions/${id}`);
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const pinDiscussion = useCallback(async (id, isPinned) => {
    setLoading(true);
    try {
      const { data } = await api.patch(`/discussions/${id}`, {
        is_pinned: !!isPinned,
      });
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const lockDiscussion = useCallback(async (id, isLocked) => {
    setLoading(true);
    try {
      const { data } = await api.patch(`/discussions/${id}`, {
        is_locked: !!isLocked,
      });
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const deleteDiscussion = useCallback(async (id) => {
    setLoading(true);
    try {
      await api.delete(`/discussions/${id}`);
      setLoading(false);
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  const deleteDiscussionPost = useCallback(async (postId) => {
    setLoading(true);
    try {
      await api.delete(`/discussions/posts/${postId}`);
      setLoading(false);
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  // Leaderboard
  const getLeaderboard = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leaderboard");
      setLoading(false);
      return data;
    } catch (e) {
      setError(e);
      setLoading(false);
      throw e;
    }
  }, []);

  // Categories (discussion)
  const getCategories = useCallback(async () => {
    const { data } = await api.get("/categories");
    return data;
  }, []);

  const createCategory = useCallback(async (payload) => {
    const { data } = await api.post("/categories", payload);
    return data;
  }, []);

  const updateCategory = useCallback(async (id, payload) => {
    const { data } = await api.put(`/categories/${id}`, payload);
    return data;
  }, []);

  const deleteCategoryHook = useCallback(async (id) => {
    await api.delete(`/categories/${id}`);
  }, []);

  // Certificates
  const getCertificates = useCallback(async (params = {}) => {
    const { data } = await api.get("/certificates", { params });
    return data;
  }, []);

  const issueCertificate = useCallback(async (payload) => {
    const { data } = await api.post("/certificates", payload);
    return data;
  }, []);

  const revokeCertificate = useCallback(async (id) => {
    const { data } = await api.post(`/certificates/${id}/revoke`);
    return data;
  }, []);

  // Students (extra)
  const updateStudent = useCallback(async (id, payload) => {
    const { data } = await api.patch(`/students/${id}`, payload);
    return data;
  }, []);

  // Generic media upload — returns { key, kind, mime, url }
  const uploadMedia = useCallback((file, onProgress) => {
    return new Promise((resolve, reject) => {
      const form = new FormData();
      form.append("file", file);
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${baseUrl}/media`);
      const token = getAuthToken();
      if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable && onProgress) {
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText));
          } catch {
            resolve({});
          }
        } else {
          reject(new Error(`Upload failed (${xhr.status})`));
        }
      };
      xhr.onerror = () => reject(new Error("Netwerkfout tijdens upload"));
      xhr.send(form);
    });
  }, []);

  return {
    loading,
    error,
    getSettings,
    updateSettings,
    getCourses,
    getCourseById,
    createCourse,
    updateCourse,
    deleteCourse,
    getModules,
    createModule,
    updateModule,
    deleteModule,
    getLessons,
    createLesson,
    updateLesson,
    deleteLesson,
    uploadLessonVideo,
    getStudents,
    getStudentProgress,
    getDiscussions,
    getDiscussion,
    pinDiscussion,
    lockDiscussion,
    deleteDiscussion,
    deleteDiscussionPost,
    getLeaderboard,
    getCategories,
    createCategory,
    updateCategory,
    deleteCategory: deleteCategoryHook,
    getCertificates,
    issueCertificate,
    revokeCertificate,
    updateStudent,
    uploadMedia,
  };
};

export default useLearningPlatform;
